'use client';

import { useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/navigation';
import { LoyaltyStatus } from './loyalty-status';
import { useLoyaltyInit } from './use-loyalty-init';
import { makePath } from '../utils';

type CheckoutLoyaltyProps = {
  /** Navigate to menu instead of opening reward sidebar */
  linkToMenu?: boolean;
  /** Show projected cart points */
  showCartProjection?: boolean;
};

export const CheckoutLoyalty = observer(
  ({ linkToMenu, showCartProjection = true }: CheckoutLoyaltyProps) => {
    const { checkout, loyalty, user } = useLoyaltyInit();
    const router = useRouter();

    const handleRewardClick = useCallback(() => {
      if (linkToMenu && checkout?.order.restaurant.id) {
        router.push(
          makePath({
            restaurantId: checkout.order.restaurant.id,
            tableCode: checkout.order.table || undefined,
            menuId: checkout.menuId,
          }),
        );
      }
    }, [
      linkToMenu,
      checkout?.menuId,
      checkout?.order.restaurant.id,
      checkout?.order.table,
      router,
    ]);

    if (!user || !loyalty?.initialized || !loyalty.isActive || !loyalty.status) return null;

    const program = loyalty.program!;

    return (
      <LoyaltyStatus
        programName={program.displayName ?? 'Rewards'}
        rewardsAvailable={loyalty.rewardsAvailable}
        pointsBalance={loyalty.pointsBalance}
        rewardItemGroupIds={program.rewardItemGroupIds ?? []}
        accentColor={checkout?.order.restaurant.restaurantGroup?.color ?? undefined}
        textColor={loyalty.textColor}
        hideProgress={loyalty.hideProgress}
        cartPoints={showCartProjection ? loyalty.cartPoints : 0}
        updating={checkout?.order.loading || loyalty.loading}
        isItemBased={loyalty.isItemBased}
        earningThreshold={loyalty.earningThreshold}
        progress={loyalty.progress}
        rewardIncludesModifiers={loyalty.rewardIncludesModifiers}
        onRewardClick={linkToMenu ? handleRewardClick : undefined}
      />
    );
  },
);
